import { Component } from '@theme/component';
import {
  calculateHeaderGroupHeight,
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
} from '@theme/utilities';
import {
  getScrollContainer,
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const SCROLL_DEADZONE = 8;
const SCROLL_HIDE_DEADZONE = 40;
const SCROLL_BOTTOM_BOUNCE_ZONE = 96;
const REDUCED_MOTION_SCROLL_DEADZONE = 14;
const REDUCED_MOTION_HIDE_DEADZONE = 48;
const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

const SCROLL_VIEWPORT_EPSILON = 1;
const SCROLL_TELEPORT = 250;

function getScrollThreshold(isScrollingUp, stickyState) {
  const reducedMotion = REDUCED_MOTION_QUERY.matches;

  if (!isScrollingUp && stickyState === 'active') {
    return reducedMotion ? REDUCED_MOTION_HIDE_DEADZONE : SCROLL_HIDE_DEADZONE;
  }

  return reducedMotion ? REDUCED_MOTION_SCROLL_DEADZONE : SCROLL_DEADZONE;
}

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;

  #intersectionObserver = null;

  #scrollContainer = null;

  #offscreen = false;

  #lastScrollTop = 0;

  #lastViewportHeight = 0;

  #activeMinScrollTop = null;

  #timeout = null;

  #scrollRafId = null;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;

      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const container = getScrollContainer();
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const rawScrollTop = getScrollTop();
    const scrollTop = Math.min(Math.max(rawScrollTop, 0), maxScrollTop);
    const isAtTop = scrollTop <= 0;
    const delta = scrollTop - this.#lastScrollTop;
    const isScrollingUp = delta < 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        if (this.dataset.scrollDirection !== 'none') this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        if (this.dataset.scrollDirection !== 'up') this.dataset.scrollDirection = 'up';
      } else {
        if (this.dataset.scrollDirection !== 'down') this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isAtTop) {
      this.#offscreen = false;
      this.#activeMinScrollTop = null;
      this.#lastScrollTop = scrollTop;
      if (this.dataset.stickyState !== 'inactive') this.dataset.stickyState = 'inactive';
      if (this.dataset.scrollDirection !== 'none') this.dataset.scrollDirection = 'none';
      return;
    }

    if (!scrollContainerMediaQuery.matches && !isAtTop) {
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight ?? 0;
      const viewportShifted =
        this.#lastViewportHeight > 0 &&
        Math.abs(viewportHeight - this.#lastViewportHeight) > SCROLL_VIEWPORT_EPSILON;
      this.#lastViewportHeight = viewportHeight;

      const overscrolling = rawScrollTop < 0 || rawScrollTop > maxScrollTop;
      const teleported = Math.abs(delta) >= SCROLL_TELEPORT;

      if (!isScrollingUp && (viewportShifted || overscrolling || teleported)) {
        this.#lastScrollTop = scrollTop;
        return;
      }
    }

    const scrollThreshold = getScrollThreshold(isScrollingUp, this.dataset.stickyState);
    if (!isAtTop && Math.abs(delta) < scrollThreshold) return;

    if (isScrollingUp) {

      this.#activeMinScrollTop = Math.min(this.#activeMinScrollTop ?? scrollTop, scrollTop);
      if (this.dataset.stickyState !== 'active') this.dataset.stickyState = 'active';
      if (this.dataset.scrollDirection !== 'up') this.dataset.scrollDirection = 'up';
    } else if (this.dataset.stickyState === 'active') {
      const activeMinScrollTop = this.#activeMinScrollTop ?? this.#lastScrollTop;
      const isBottomBounce = maxScrollTop - activeMinScrollTop <= SCROLL_BOTTOM_BOUNCE_ZONE;
      if (!isAtTop && (isBottomBounce || scrollTop - activeMinScrollTop < scrollThreshold)) return;

      this.#activeMinScrollTop = null;
      if (this.dataset.scrollDirection !== 'none') this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.#activeMinScrollTop = null;
      if (this.dataset.scrollDirection !== 'none') this.dataset.scrollDirection = 'none';
      if (this.dataset.stickyState !== 'idle') this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#lastViewportHeight = window.visualViewport?.height ?? window.innerHeight ?? 0;
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

document.addEventListener(
  'open',
  (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || !target.matches('shopify-account')) return;

    const bottom = Math.max(0, Math.round(target.getBoundingClientRect().bottom));
    target.style.setProperty('--account-dialog-top', `${bottom}px`);
  },
  true
);

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {

      const currentHeader = document.querySelector('#header-component');
      const headerGroupHeight = calculateHeaderGroupHeight(
        currentHeader instanceof HTMLElement ? currentHeader : null,
        headerGroup,
      );

      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {

          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
